import { Button } from "@/components/ui/button";
import { resolveAttachmentUrl } from "@/lib/api";
import {
  Plus, Trash2, RefreshCw, Pencil, Mail, GraduationCap, Sparkles, School,
  ChevronDown, ChevronRight, Paperclip, ExternalLink,
} from "lucide-react";

export function SchoolAccountsSection({ ctx }) {
  const {
    // Värmdö
    varmdoCreds, varmdoBusy, expandedCredId, expandedCourseId,
    credCourses, courseItems,
    toggleExpandCred, toggleExpandCourse,
    openEditVarmdo, openCreateVarmdo, syncVarmdo, syncVarmdoGmail, removeVarmdo,
    setAttachmentPreview,
    // Vklass
    vklassAccounts, vklassBusy,
    openEditVklass, openCreateVklass, syncVklassAccount, removeVklassAccount,
  } = ctx;

  return (
    <>
      <div className="mb-6" data-testid="varmdo-section">
        <h3 className="font-display text-base font-medium mb-2 flex items-center gap-2">
          <GraduationCap size={16} /> Classroom-konton
        </h3>
        <div className="card-soft p-4">
          <p className="text-xs text-[color:var(--text-secondary)] mb-3">
            Loggar in som eleven via Värmdös portal och hämtar Classroom-uppgifter direkt.
            Använd detta när Värmdö blockerar OAuth-anslutning. Inloggningen sparas <span className="font-semibold">krypterat</span> (AES) och uppdateras automatiskt var 4:e timme.
          </p>
          {varmdoCreds.length === 0 ? (
            <p className="text-xs text-[color:var(--text-tertiary)] mb-3 italic">
              Inga elever anslutna än.
            </p>
          ) : (
            <ul className="space-y-2 mb-3">
              {varmdoCreds.map((c) => (
                <li key={c.id} data-testid={`varmdo-cred-${c.id}`}>
                  <div className="flex items-start gap-2.5">
                    <button
                      onClick={() => toggleExpandCred(c.id)}
                      className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)] mt-0.5"
                      title={expandedCredId === c.id ? "Dölj kurser" : "Visa kurser"}
                      data-testid={`varmdo-expand-${c.id}`}
                    >
                      {expandedCredId === c.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <div className="w-8 h-8 rounded-full bg-[color:var(--moss)] flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                      {c.label?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.label}</p>
                      <p className="text-[10px] text-[color:var(--text-tertiary)] truncate">{c.username}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: c.last_status === "ok" ? "var(--moss)" : c.last_status === "syncing" ? "var(--jarvis)" : "#DC2626" }}>
                        {c.last_status === "syncing"
                          ? "Synkar i bakgrunden…"
                          : c.last_status === "ok"
                          ? `${c.enrolled_courses ?? c.last_courses_count ?? "?"} kurser${c.last_courses_count != null && c.enrolled_courses != null && c.enrolled_courses !== c.last_courses_count ? ` · ${c.last_courses_count} med uppgifter` : ""} · senast ${c.last_check ? new Date(c.last_check).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) : "—"}`
                          : c.last_error || "Fel"}
                      </p>
                    </div>
                    <button
                      onClick={() => openEditVarmdo(c)}
                      className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)]"
                      title="Redigera"
                      data-testid={`varmdo-edit-${c.id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => syncVarmdo(c.id, false)}
                      disabled={varmdoBusy}
                      className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)] disabled:opacity-50"
                      title="Snabb-sync (Att göra ~15s)"
                      data-testid={`varmdo-sync-${c.id}`}
                    >
                      <RefreshCw size={14} />
                    </button>
                    <button
                      onClick={() => syncVarmdo(c.id, true)}
                      disabled={varmdoBusy}
                      className="p-1.5 rounded text-[color:var(--jarvis)] hover:text-[#1f421f] disabled:opacity-50"
                      title="Djup-sync inkl. flödesposter (~75s)"
                      data-testid={`varmdo-deepsync-${c.id}`}
                    >
                      <Sparkles size={14} />
                    </button>
                    <button
                      onClick={() => syncVarmdoGmail(c.id)}
                      disabled={varmdoBusy}
                      className="p-1.5 rounded text-[#E07A5F] hover:text-[#b85b40] disabled:opacity-50"
                      title="Synka mejl senaste 14 dagar (~2 min)"
                      data-testid={`varmdo-gmail-sync-${c.id}`}
                    >
                      <Mail size={14} />
                    </button>
                    <button
                      onClick={() => removeVarmdo(c.id)}
                      className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[#DC2626]"
                      title="Ta bort"
                      data-testid={`varmdo-remove-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Expandera kurslista */}
                  {expandedCredId === c.id && (
                    <div className="ml-8 mt-2 pl-3 border-l border-[color:var(--bg-elevated)] space-y-1" data-testid={`varmdo-courses-${c.id}`}>
                      {!credCourses[c.id] ? (
                        <p className="text-[11px] text-[color:var(--text-tertiary)] py-2">Laddar kurser…</p>
                      ) : credCourses[c.id].length === 0 ? (
                        <p className="text-[11px] text-[color:var(--text-tertiary)] py-2 italic">Inga kurser sparade än — kör djup-sync</p>
                      ) : (
                        credCourses[c.id].map((course) => {
                          const courseKey = `${c.id}::${course.course_id}`;
                          const isExpanded = expandedCourseId === courseKey;
                          const isExpandable = course.has_items;
                          return (
                            <div key={course.course_id} data-testid={`course-row-${course.course_id}`}>
                              <button
                                onClick={() => isExpandable ? toggleExpandCourse(c.id, course.course_id) : null}
                                disabled={!isExpandable}
                                className={`w-full flex items-center gap-2 py-1.5 px-2 rounded-lg text-left ${isExpandable ? "hover:bg-[color:var(--bg-elevated)] cursor-pointer" : "opacity-60"}`}
                                data-testid={`course-toggle-${course.course_id}`}
                              >
                                {isExpandable ? (
                                  isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />
                                ) : <span className="w-[11px]" />}
                                <span className="text-xs font-medium flex-1 truncate">{course.course_name}</span>
                                {course.new_count > 0 && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E07A5F] text-white font-semibold">
                                    {course.new_count} nytt
                                  </span>
                                )}
                                {course.stream_count + course.coursework_count > 0 && (
                                  <span className="text-[9px] text-[color:var(--text-tertiary)]">
                                    {course.stream_count + course.coursework_count}
                                  </span>
                                )}
                              </button>
                              {isExpanded && courseItems[courseKey] && (
                                <div className="ml-5 mt-1 mb-2 space-y-1.5">
                                  {[
                                    ...(courseItems[courseKey].coursework || []).map(i => ({ ...i, kind: "coursework" })),
                                    ...(courseItems[courseKey].stream || []).map(i => ({ ...i, kind: "stream" })),
                                  ].sort((a,b) => (b.published_at || b.first_seen_at || "").localeCompare(a.published_at || a.first_seen_at || "")).map((item, idx) => (
                                    <div
                                      key={`${item.kind}-${item.id || idx}`}
                                      className="bg-[color:var(--bg-elevated)] rounded-lg p-2"
                                      data-testid={`course-item-${item.id || idx}`}
                                    >
                                      <div className="flex items-start gap-2">
                                        <span className={`text-[8px] px-1 py-0.5 rounded font-semibold uppercase tracking-wide ${item.kind === "coursework" ? "bg-[#F0F6E6] text-[color:var(--moss)]" : "bg-[#E8EEF3] text-[#3D5A80]"}`}>
                                          {item.kind === "coursework" ? "Uppg" : "Flöde"}
                                        </span>
                                        {item.is_new && (
                                          <span className="text-[8px] px-1 py-0.5 rounded bg-[#E07A5F] text-white font-semibold">NY</span>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[11px] font-medium leading-snug">
                                            {item.title || item.content?.slice(0, 100)}
                                          </p>
                                          <p className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5">
                                            {item.author && `${item.author} · `}{item.due_text || item.date_text}
                                          </p>
                                          {item.kind === "stream" && item.content && (
                                            <p className="text-[10px] text-[color:var(--text-secondary)] mt-1 line-clamp-3">{item.content}</p>
                                          )}
                                          {(item.attachments || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                              {item.attachments.map((att, i) => {
                                                const canPreview = att.proxied && /\.(pdf|png|jpe?g|gif|webp)(\?|$)/i.test(att.title || att.url || "");
                                                const href = resolveAttachmentUrl(att.url);
                                                return (
                                                  <a
                                                    key={att.url || `att-${i}-${(att.title || '').slice(0,20)}`}
                                                    href={href}
                                                    target={canPreview ? undefined : "_blank"}
                                                    rel="noreferrer"
                                                    onClick={(e) => {
                                                      if (canPreview) {
                                                        e.preventDefault();
                                                        setAttachmentPreview({ url: href, title: att.title || "Bilaga", mime: att.mime });
                                                      }
                                                    }}
                                                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border border-[color:var(--moss)] text-[color:var(--moss)] hover:bg-[#F0F6E6]"
                                                    data-testid={`attachment-${i}`}
                                                  >
                                                    <Paperclip size={9} />
                                                    <span className="truncate max-w-[140px]">{att.title}</span>
                                                  </a>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {item.link && (
                                            <a href={item.link} target="_blank" rel="noreferrer" className="text-[10px] text-[color:var(--jarvis)] hover:underline mt-1 inline-flex items-center gap-0.5">
                                              Öppna <ExternalLink size={9} />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <Button
            onClick={openCreateVarmdo}
            size="sm"
            className="w-full rounded-xl bg-[color:var(--moss)] hover:bg-[#1f421f]"
            data-testid="varmdo-add-button"
          >
            <Plus size={14} className="mr-1" /> Lägg till Classroom-konto
          </Button>
        </div>
      </div>

      {/* Vklass-konton (förälder + elever, unified) */}
      <div className="mb-6" data-testid="vklass-accounts-section">
        <h3 className="font-display text-base font-medium mb-2 flex items-center gap-2">
          <School size={16} className="text-[color:var(--moss)]" /> Vklass-konton
        </h3>
        <div className="card-soft p-4">
          <p className="text-xs text-[color:var(--text-secondary)] mb-3">
            Lägg till en <span className="font-semibold">inloggning</span> så hämtas schema automatiskt.
            Alla lösenord sparas krypterat (AES).
          </p>
          {vklassAccounts.length === 0 ? (
            <p className="text-xs text-[color:var(--text-tertiary)] mb-3 italic">
              Inga Vklass-konton anslutna än.
            </p>
          ) : (
            <ul className="space-y-2 mb-3">
              {vklassAccounts.map((acc) => (
                <li
                  key={`${acc.kind}-${acc.id}`}
                  className="flex items-start gap-2.5"
                  data-testid={`vklass-acc-${acc.id}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0 ${
                      acc.kind === "parent" ? "bg-[#82A0BC]" : "bg-[color:var(--moss)]"
                    }`}
                  >
                    {acc.label?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{acc.label}</p>
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                          acc.kind === "parent" ? "bg-[#E8EEF3] text-[#3D5A80]" : "bg-[#F0F6E6] text-[color:var(--moss)]"
                        }`}
                      >
                        {acc.kind === "parent" ? "Förälder" : "Elev"}
                      </span>
                    </div>
                    <p className="text-[10px] text-[color:var(--text-tertiary)] truncate">{acc.username}</p>
                    <p
                      className="text-[10px] mt-0.5"
                      style={{ color: acc.last_status === "ok" ? "var(--moss)" : acc.last_status === "error" ? "#DC2626" : "var(--text-tertiary)" }}
                    >
                      {acc.last_status === "ok" && acc.last_check && (
                        <>
                          {acc.stats_count != null ? `${acc.stats_count} ${acc.stats_label} · ` : ""}
                          Senast {new Date(acc.last_check).toLocaleString("sv-SE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </>
                      )}
                      {acc.last_status === "error" && (acc.last_error || "Fel vid senaste sync")}
                      {!acc.last_status && "Aldrig synkat"}
                    </p>
                  </div>
                  <button
                    onClick={() => openEditVklass(acc)}
                    className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)]"
                    title="Redigera"
                    data-testid={`vklass-acc-edit-${acc.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => syncVklassAccount(acc)}
                    disabled={vklassBusy}
                    className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)] disabled:opacity-50"
                    title="Synka nu"
                    data-testid={`vklass-acc-sync-${acc.id}`}
                  >
                    <RefreshCw size={14} />
                  </button>
                  <button
                    onClick={() => removeVklassAccount(acc)}
                    className="p-1.5 rounded text-[color:var(--text-tertiary)] hover:text-[#DC2626]"
                    title="Ta bort"
                    data-testid={`vklass-acc-remove-${acc.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button
            onClick={openCreateVklass}
            size="sm"
            className="w-full rounded-xl bg-[color:var(--moss)] hover:bg-[#1f421f]"
            data-testid="vklass-add-account-button"
          >
            <Plus size={14} className="mr-1" /> Lägg till Vklass-användare
          </Button>
        </div>
      </div>
    </>
  );
}
